import React, { useState } from "react";
import { BarChart3, Clock, Coins, Network, Target } from "lucide-react";
import { EaMiniArea, EaMiniBars } from "@/components/energy-analytics/analytics/EaMiniCharts";
import { EaSelfSection } from "@/components/energy-analytics/analytics/EaSelfCard";
import { eaParams, useEa, useGasUnit } from "@/components/energy-analytics/analytics/EaContext";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaEmptyState, EaKpiCard, EaNote, EaPageHeader, EaUnitToggle, EaGasUnitToggle, SECTION_STYLE, EaBudgetBar } from "@/components/energy-analytics/analytics/EaUi";
import { EaMonthlySpendChart } from "@/components/energy-analytics/analytics/EaMonthlySpendChart";
import { EaConsumptionCharts } from "@/components/energy-analytics/analytics/EaConsumptionCharts";
import { EaBudgetForecastChart } from "@/components/energy-analytics/analytics/EaBudgetForecastChart";
import { EaAvgCostCard } from "@/components/energy-analytics/analytics/EaAvgCostCard";
import { EaPointPicker, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { COLORS, MONTHS_IT, PCS_STD, fmtEur, fmtPower, fmtQty, fmtSignedPct } from "@/components/energy-analytics/analytics/eaFormat";

function SpendSplit({ d }) {
  const p = d.power.spend_share_pct, g = d.gas.spend_share_pct;
  if (p == null && g == null) return null;
  return (
    <div className="space-y-2 mt-1" data-testid="ea-spend-split">
      <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
        <div style={{ width: `${p || 0}%`, background: SECTION_STYLE.power.color }} />
        <div style={{ width: `${g || 0}%`, background: SECTION_STYLE.gas.color }} />
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
        {d.power.points > 0 && <span data-testid="ea-spend-power"><b className="text-slate-900">Power</b> {fmtEur(d.power.spend_ytd)} · {fmtQty(p, 1, "%")}</span>}
        {d.gas.points > 0 && <span data-testid="ea-spend-gas"><b className="text-slate-900">Gas</b> {fmtEur(d.gas.spend_ytd)} · {fmtQty(g, 1, "%")}</span>}
      </div>
    </div>
  );
}

const mon = (b) => (b?.points ? b.monthly || [] : []);
const spendSeries = (b) => mon(b).map((m) => ({ v: m.spend ?? null }));
const consSeries = (b, k = 1) => mon(b).map((m) => ({ v: (m.consumption_actual ?? m.consumption_forecast ?? 0) * k, fc: m.consumption_actual == null }));
const fcSeries = (blocks) => MONTHS_IT.map((_, i) => {
  const ms = blocks.map((b) => mon(b)[i]).filter(Boolean);
  return { v: ms.reduce((a, m) => a + (m.spend ?? m.spend_forecast ?? 0), 0), fc: ms.some((m) => m.spend == null) };
});
const costSeries = (b, k) => mon(b).map((m) => ({ v: m.spend != null && m.consumption_actual ? (m.spend / m.consumption_actual) * k : null }));
const sumSeries = (a, b) => MONTHS_IT.map((_, i) => ({ v: (a[i]?.v || 0) + (b[i]?.v || 0), fc: a[i]?.fc || b[i]?.fc }));

function invoicedText(b) {
  return `${b.months_invoiced} su 12${b.last_invoiced_month ? ` · fino a ${MONTHS_IT[b.last_invoiced_month - 1]}` : ""}`;
}

function Section({ kind, title, children, testid, cols = "xl:grid-cols-4", units }) {
  const { Icon, bg } = SECTION_STYLE[kind];
  return (
    <section className="space-y-3" data-testid={testid} data-pdf="section">
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: bg }}><Icon size={16} /></span>
        <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
        {units && <div className="ml-1" data-testid={`${testid}-units`}>{units}</div>}
      </div>
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols} gap-4`}>{children}</div>
    </section>
  );
}

function BudgetCards({ b, bg, prefix, testid, fc, color, light }) {
  const delta = b.budget != null && b.forecast != null ? b.forecast - b.budget : null;
  return <>
    <EaKpiCard band={bg} icon={Target} label={`Budget annuale${prefix}`} value={fmtEur(b.budget)} testid={`${testid}-budget`}
      sub={b.budget == null ? "Non presente nel file per questo anno" : "Valore dichiarato nel Report, anno intero"}>
      <EaBudgetBar spent={b.spend_ytd} budget={b.budget} color={color} testid={`${testid}-budget-bar`} />
    </EaKpiCard>
    <EaKpiCard band={bg} icon={BarChart3} label={`Forecast annuale${prefix}`} value={fmtEur(b.forecast)} testid={`${testid}-forecast`}
      chart={fc && <EaMiniBars data={fc} color={color} light={light} testid={`${testid}-forecast-chart`} />}
      sub={delta != null ? `${delta >= 0 ? "+" : ""}${fmtEur(delta)} vs Budget (${fmtSignedPct(b.budget ? (delta / b.budget) * 100 : null)})`
        : b.budget == null && b.forecast != null ? "Anno chiuso: consuntivo fatturato (+ ricostruzione Adybo dei mesi non fatturati)" : "Valore dichiarato nel Report"} />
  </>;
}

function TotalConsCard({ d, band }) {
  const u = "MWh";
  const pk = d.power.points ? d.power.consumption_actual || 0 : 0, gk = d.gas.points ? (d.gas.consumption_actual || 0) * PCS_STD : 0;
  return (
    <EaKpiCard band={band} icon={BarChart3} label="Consumo totale Power + Gas" value={fmtPower(pk + gk, u)} testid="ea-kpi-cons-total"
      chart={<EaMiniBars data={sumSeries(consSeries(d.power), consSeries(d.gas, PCS_STD))} color={COLORS.total} light={COLORS.totalLight} testid="ea-kpi-cons-total-chart" />}
      sub={`Consuntivo · Power ${fmtPower(pk, u)} + Gas ${fmtPower(gk, u)} (${fmtQty(d.gas.consumption_actual, 0, "Smc")} × PCS standard ${fmtQty(PCS_STD, 4)} kWh/Smc)`} />
  );
}

function KpiGrid({ d, unit, setUnit, include, setInclude, params }) {
  const { power: pw, gas } = d;
  const g = useGasUnit();
  const T = SECTION_STYLE.overview.bg, P = SECTION_STYLE.power.bg, G = SECTION_STYLE.gas.bg;
  return (
    <div className="space-y-8" data-testid="ea-kpi-grid">
      <Section kind="overview" title="Overview Totali Commodity" testid="ea-sec-total">
        <EaKpiCard band={T} icon={Coins} label="Spesa fatturata totale (YTD)" value={fmtEur(d.spend_total_ytd)} testid="ea-kpi-spend-total"
          sub="Solo fatture del fornitore. I mesi senza fattura non sono conteggiati.">
          <SpendSplit d={d} />
        </EaKpiCard>
        <TotalConsCard d={d} band={T} />
        <BudgetCards b={{ budget: d.budget_total, forecast: d.forecast_total, spend_ytd: d.spend_total_ytd }} bg={T} prefix=" totale" testid="ea-kpi"
          fc={fcSeries([pw, gas])} color={COLORS.total} light={COLORS.totalLight} />
      </Section>
      {pw.points > 0 && (
        <Section kind="power" title="Totale Energia Elettrica" testid="ea-sec-power" cols="xl:grid-cols-3" units={<EaUnitToggle unit={unit} setUnit={setUnit} />}>
          <EaKpiCard band={P} icon={Coins} label="Spesa fatturata Power (YTD)" value={fmtEur(pw.spend_ytd)} testid="ea-kpi-spend-power" sub={`Fatture ${invoicedText(pw)}`}
            chart={<EaMiniArea data={spendSeries(pw)} color={COLORS.power} testid="ea-kpi-spend-power-chart" />} />
          <EaKpiCard band={P} icon={BarChart3} chart={<EaMiniBars data={consSeries(pw)} color={COLORS.power} light={COLORS.powerLight} testid="ea-kpi-cons-power-chart" />} label="Consumi Power (consuntivo)" value={fmtPower(pw.consumption_actual, unit)} testid="ea-kpi-cons-power"
            sub={`${pw.months_actual} mesi a consuntivo · anno con previsionale ${fmtPower(pw.consumption_year, unit)}${pw.self_consumption ? (include ? " · incluso autoconsumo" : " · solo prelievi di rete") : ""}`} />
          <EaAvgCostCard band={P} icon={Clock} block={pw} label="Costo medio totale Power" unit="€/MWh" testid="ea-kpi-avg-power"
            chart={<EaMiniArea data={costSeries(pw, 1000)} color={COLORS.power} testid="ea-kpi-avg-power-chart" />} />
          <EaKpiCard band={P} icon={Network} label="POD" value={d.counts.pod} testid="ea-kpi-points-power" sub="Punti di fornitura Power" />
          <BudgetCards b={pw} bg={P} prefix=" Power" testid="ea-kpi-power" fc={fcSeries([pw])} color={COLORS.power} light={COLORS.powerLight} />
        </Section>
      )}
      {pw.self_consumption && <EaSelfSection sc={pw.self_consumption} band={P} unit={unit} include={include} setInclude={setInclude} params={params} />}
      {gas.points > 0 && (
        <Section kind="gas" title="Totale Gas Naturale" testid="ea-sec-gas" cols="xl:grid-cols-3" units={<EaGasUnitToggle />}>
          <EaKpiCard band={G} icon={Coins} label="Spesa fatturata Gas (YTD)" value={fmtEur(gas.spend_ytd)} testid="ea-kpi-spend-gas" sub={`Fatture ${invoicedText(gas)}`}
            chart={<EaMiniArea data={spendSeries(gas)} color={COLORS.gas} testid="ea-kpi-spend-gas-chart" />} />
          <EaKpiCard band={G} icon={BarChart3} chart={<EaMiniBars data={consSeries(gas)} color={COLORS.gas} light={COLORS.gasLight} testid="ea-kpi-cons-gas-chart" />} label="Consumi Gas (consuntivo)" value={g.f(gas.consumption_actual)} testid="ea-kpi-cons-gas"
            sub={`${gas.months_actual} mesi a consuntivo · anno con previsionale ${g.f(gas.consumption_year)}`} />
          <EaAvgCostCard band={G} icon={Clock} chart={<EaMiniArea data={costSeries(gas, 100)} color={COLORS.gas} testid="ea-kpi-avg-gas-chart" />} block={gas} label="Costo medio totale Gas" unit={g.pu("c€/Smc")} conv={(v) => g.p(v)} testid="ea-kpi-avg-gas" />
          <EaKpiCard band={G} icon={Network} label="Utenze gas" value={d.counts.gas_points} testid="ea-kpi-points-gas" sub="Punti di fornitura Gas" />
          <BudgetCards b={gas} bg={G} prefix=" Gas" testid="ea-kpi-gas" fc={fcSeries([gas])} color={COLORS.gas} light={COLORS.gasLight} />
        </Section>
      )}
    </div>
  );
}

export default function EaOverview() {
  const { unit, setUnit, filters, gasUnit } = useEa();
  const codes = useSelectedCodes();
  const [include, setIncludeState] = useState(() => localStorage.getItem("ea_include_self") === "1");
  const setInclude = (v) => { localStorage.setItem("ea_include_self", v ? "1" : "0"); setIncludeState(v); };
  const { data: d, loading, error } = useEaData("/ea/overview", { codes, self_consumption: include || undefined });
  const pw = d?.power || {}, gas = d?.gas || {};
  const months = d?.has_data && (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-right" data-testid="ea-kpi-months-invoiced">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Mesi fatturati</div>
      <div className="text-sm font-semibold text-slate-900 font-mono-nums">{[pw.points && `Power ${pw.months_invoiced}/12`, gas.points && `Gas ${gas.months_invoiced}/12`].filter(Boolean).join(" · ")}</div>
      <div className="text-[11px] text-slate-500">{[pw.points && `Power: ${invoicedText(pw)}`, gas.points && `Gas: ${invoicedText(gas)}`].filter(Boolean).join(" — ")}</div>
    </div>
  );
  const header = <EaPageHeader title="Portfolio complessivo" subtitle={`Spesa, consumi e budget di tutti i punti Power e Gas${d?.year ? ` · anno ${d.year}` : ""}.`} aside={months}
    right={<EaPointPicker />} />;
  if (loading) return <div className="space-y-6">{header}<div className="text-sm text-slate-400" data-testid="ea-loading">Caricamento…</div></div>;
  if (error) return <div className="space-y-6">{header}<EaEmptyState title="Dati non disponibili" text={error} testid="ea-overview-error" /></div>;
  if (!d.has_data) return <div className="space-y-6">{header}<EaEmptyState title="Nessun dato per l'anno selezionato" testid="ea-overview-empty" /></div>;
  return (
    <div className="space-y-6" data-testid="ea-overview-page">
      {header}
      <KpiGrid d={d} unit={unit} setUnit={setUnit} include={include} setInclude={setInclude} params={eaParams(filters, { codes })} />
      <EaChartCard title="Spesa energetica mensile" subtitle="Barre piene = fatture del fornitore. Barre tratteggiate = mesi in attesa di fattura, valorizzati con la stima forecast del Report (non è spesa fatturata)." testid="ea-card-monthly-spend">
        <EaMonthlySpendChart power={d.power} gas={d.gas} />
      </EaChartCard>
      <EaConsumptionCharts power={d.power} gas={d.gas} unit={unit} unitToggle={<EaUnitToggle unit={unit} setUnit={setUnit} prefix="ea-chart-unit" />} gasUnit={gasUnit} gasToggle={<EaGasUnitToggle prefix="ea-chart-gas-unit" />} />
      <EaChartCard title="Budget vs Forecast vs Spesa fatturata" subtitle="Budget e Forecast si riferiscono all'anno intero. La spesa fatturata YTD è parziale e non va confrontata come valore annuale." testid="ea-card-budget-forecast">
        <EaBudgetForecastChart data={d} />
        <div className="mt-3"><EaNote testid="ea-ytd-note">Spesa fatturata YTD: somma delle sole fatture ricevute finora. Power {d.power.points ? invoicedText(d.power) : "—"}; Gas {d.gas.points ? invoicedText(d.gas) : "—"}.</EaNote></div>
      </EaChartCard>
    </div>
  );
}
